/**
 * Weapon-profile freezing. A spawn adapter resolves a mech's equipped weapons out of the
 * compendium **once at spawn** and freezes a {@link WeaponProfile} for each onto the
 * combatant `base`. The resolver thereafter reads damage dice/type + mitigation
 * tags off the frozen profile and **never touches the compendium** — combat is
 * self-contained on tactical state.
 *
 * This module is the parse layer: compendium weapon data → frozen profile. It
 * lives in `@lancer-vtt/rules` (the authority side that owns the compendium and
 * `compute`); the resulting plain-data {@link WeaponProfile} (typed in
 * `@lancer-vtt/domain`) is what the reducer-adjacent code sees.
 *
 * **Compendium weapon shape** (`@massif/lancer-data`, via the compendium's
 * `.passthrough()`):
 *   - `damage: [{ type: "Kinetic", val: "2d6" }]` — `val` is `"NdM"`, `"NdM+K"`,
 *     a bare `"K"`, or a plain number; `type` is capitalized.
 *   - `tags: [{ id: "tg_ap" }, { id: "tg_reliable", val: 2 }, { id: "tg_overkill" }]`.
 *
 * **A tag `val` is not always a number.** Two distinct non-numeric shapes occur in real data
 * and they are handled differently on purpose:
 *   - **Tier notation** — an NPC weapon's `"{2/3/4}"` (Assault/Archer/Rainmaker's `tg_reliable`),
 *     the printed statblock's own per-tier triple. Resolved against the spawning NPC's tier by
 *     {@link tierNotationVal}. Getting this wrong silently zeroed the tag.
 *   - **Dice expressions** — `tg_limited`'s `"1d6+2"`/`"2d6"` on `mw_prototype_1/2/3`, a charge
 *     pool RAW rolls once. **Still truncated by `parseInt` here** (`"1d6+2"` → 1, `"2d6"` → 2) —
 *     a known defect with its own scope, deliberately NOT folded into the tier-notation path.
 *     Fixing it means rolling a pool once at freeze time, which is a different change from
 *     indexing a printed triple; don't conflate them.
 * **Every** `damage[]` entry freezes: the first to {@link WeaponProfile.damage} (the
 * to-hit-linked primary), the rest to {@link WeaponProfile.riders} in source order (Aegis Light
 * Laser's Burn rider, Pyro Napalm Bomb's Heat rider, Ultra Hellfire Projector's mixed
 * scalar/per-tier pair).
 */

import type { DamageType, FrozenNpcFeature, RangeKind, WeaponDamage, WeaponProfile, WeaponRange } from "@lancer-vtt/domain";
import type { Bonus } from "../bonus";
import type { NpcTier } from "./spawn";

/** The (loosely-typed, passthrough) compendium weapon fields the freeze reads. */
export type RawWeapon = {
  id?: string;
  name?: string;
  /** The weapon's **mount size** (`"Auxiliary" | "Main" | "Heavy" | "Superheavy"`), frozen for the Barrage legality gate. */
  mount?: unknown;
  /**
   * The weapon's **class** (`"CQB" | "Cannon" | "Launcher" | "Melee" | "Nexus" | "Rifle" |
   * "Special" | "Spool Weapon" | "???"`), frozen for the `weapon_type` predicate op.
   * **Same field name, different vocabulary on pilot
   * gear** — a `pilot_gear` compendium entry's `type` is its gear-kind classifier
   * (`"Weapon"`/`"Armor"`/`"Gear"`), not a weapon class; {@link normalizeWeaponType}'s
   * closed-set validation is what keeps that value from leaking onto {@link WeaponProfile.type}.
   */
  type?: unknown;
  /** lancer-data's per-weapon or per-profile `skirmish` flag; only `true` freezes ({@link WeaponProfile.skirmish}). */
  skirmish?: unknown;
  /** lancer-data's `no_attack` flag; only `true` freezes ({@link WeaponProfile.noAttack}). */
  no_attack?: unknown;
  damage?: Array<{ type?: unknown; val?: unknown }>;
  tags?: Array<{ id?: unknown; val?: unknown }>;
  /**
   * **Range / area** entries — `[{type:"Range",val:10},{type:"Blast",val:1}]`. The
   * type is a capitalized pattern name (Range/Threat/Blast/Line/Cone/Burst) and `val`
   * is the spaces count (number or numeric string). Frozen onto {@link WeaponProfile.range}
   * to drive the VTT's attack-range overlay; alternate `profiles` carry their own.
   */
  range?: Array<{ type?: unknown; val?: unknown }>;
  /**
   * **Alternate firing profiles** — a multi-profile weapon
   * (the Apocalypse Rail's Charge-Rail tiers; Assault Cannon's Spin-Up) carries its
   * other selectable modes here, each its own `{name, damage, tags}`. Frozen at spawn
   * as {@link WeaponProfile.profiles}, each with a derived `${baseId}__${slug(name)}` id
   * the effects-engine overlay's `select` directive references.
   */
  profiles?: Array<{
    name?: unknown;
    skirmish?: unknown;
    damage?: Array<{ type?: unknown; val?: unknown }>;
    tags?: Array<{ id?: unknown; val?: unknown }>;
    range?: Array<{ type?: unknown; val?: unknown }>;
  }>;
}

/** Derive a stable profile id from the base weapon id + the profile name (slugged). */
export function weaponProfileId(baseId: string, name: string): string {
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  return `${baseId}__${slug}`;
}

/** Normalize a compendium damage `type` string to a {@link DamageType} (lowercased). */
export function normalizeDamageType(type: unknown): DamageType {
  const t = typeof type === "string" ? type.toLowerCase() : "";
  switch (t) {
    case "kinetic":
    case "energy":
    case "explosive":
    case "burn":
    case "heat":
    case "variable":
      return t;
    default:
      // Unknown/absent damage type → kinetic (the ordinary-armor-mitigated default).
      // Exotic types are out of scope; this keeps the parse total.
      return "kinetic";
  }
}

/** The closed set of compendium weapon-class values (`@massif/lancer-data`'s `weapons.json`). */
const WEAPON_TYPE_VALUES = new Set([
  "CQB",
  "Cannon",
  "Launcher",
  "Melee",
  "Nexus",
  "Rifle",
  "Special",
  "Spool Weapon",
  "???",
]);

/**
 * Normalize a compendium weapon `type` to a known weapon-class string, verbatim (title
 * case, unlike {@link normalizeDamageType}/{@link normalizeRangeKind} — content authors
 * match it against the SAME spelling the compendium prints, e.g. `weapon_type: "Rifle"`).
 * `undefined` for anything outside the closed set — **this is the landmine guard**: a
 * `pilot_gear` compendium entry reuses the field name `type` for its own gear-kind
 * classifier (`"Weapon"`/`"Armor"`/`"Gear"`), which is not a member of this set, so it
 * normalizes away rather than freezing a false weapon class onto a pilot-gear weapon
 * (RAW pilot weapons carry no Rifle/CQB-style taxonomy at all — the correct absence).
 */
export function normalizeWeaponType(type: unknown): string | undefined {
  return typeof type === "string" && WEAPON_TYPE_VALUES.has(type) ? type : undefined;
}

/** Normalize a compendium range `type` to a {@link RangeKind} (lowercased), or `null` for an unknown pattern. */
export function normalizeRangeKind(type: unknown): RangeKind | null {
  const t = typeof type === "string" ? type.toLowerCase() : "";
  switch (t) {
    case "range":
    case "threat":
    case "blast":
    case "line":
    case "cone":
    case "burst":
      return t;
    default:
      // Unknown pattern (a homebrew/exotic range type) — dropped, never guessed.
      return null;
  }
}

/** Coerce a range `val` (number or numeric string) to a non-negative integer count of spaces; `0` if absent/malformed. */
function rangeValOf(val: unknown): number {
  if (typeof val === "number" && Number.isFinite(val)) return Math.trunc(val);
  if (typeof val === "string") {
    const n = parseInt(val, 10);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

/**
 * Parse a compendium `range` array into frozen {@link WeaponRange} entries — known
 * patterns only (`normalizeRangeKind`), with a positive `val`. An unknown type or a
 * `0`/absent val carries no template and is dropped, so a weapon with no usable range
 * freezes to `[]` (the caller then omits the field entirely).
 */
export function parseRange(raw: RawWeapon["range"]): WeaponRange[] {
  const out: WeaponRange[] = [];
  for (const r of raw ?? []) {
    const kind = normalizeRangeKind(r.type);
    if (!kind) continue;
    const val = rangeValOf(r.val);
    if (val <= 0) continue;
    out.push({ kind, val });
  }
  return out;
}

/**
 * Parse a damage `val` (`"2d6"`, `"1d3+3"`, `"3"`, or a number) into
 * `{ count, sides, bonus }`. A bare number / non-dice string parses to a flat
 * bonus (`{ count: 0, sides: 0, bonus: N }`). Malformed input → all-zero (a
 * weapon that deals nothing; the resolver still records a `damage` step).
 */
export function parseDamageVal(val: unknown): { count: number; sides: number; bonus: number } {
  if (typeof val === "number" && Number.isFinite(val)) {
    return { count: 0, sides: 0, bonus: Math.trunc(val) };
  }
  if (typeof val !== "string") return { count: 0, sides: 0, bonus: 0 };
  const s = val.trim().toLowerCase();
  // NdM, NdM+K, NdM-K, or a bare integer.
  const dice = s.match(/^(\d+)d(\d+)\s*([+-]\s*\d+)?$/);
  if (dice) {
    const count = parseInt(dice[1]!, 10);
    const sides = parseInt(dice[2]!, 10);
    const bonus = dice[3] ? parseInt(dice[3]!.replace(/\s+/g, ""), 10) : 0;
    return { count, sides, bonus };
  }
  const flat = s.match(/^([+-]?\d+)$/);
  if (flat) return { count: 0, sides: 0, bonus: parseInt(flat[1]!, 10) };
  return { count: 0, sides: 0, bonus: 0 };
}

/**
 * Freeze a weapon/feature's **tag refs** (id + numeric val) — the shared shape both
 * {@link freezeProfileCore} (effects-engine E3) and the NPC display features carry.
 * Only well-formed string-id refs survive; a numeric `val` is kept (`{ id, val }`),
 * a zero/absent one collapses to a bare `{ id }`. `tier` resolves an NPC's tier-notation
 * val (see {@link tierNotationVal}) — pass it wherever the owning combatant's tier is
 * known, omit it on the tier-less mech/pilot-gear paths.
 */
export function freezeTagRefs(
  rawTags: RawWeapon["tags"],
  tier?: NpcTier,
): { id: string; val?: number }[] {
  return (rawTags ?? [])
    .filter((t): t is { id: string; val?: unknown } => typeof t.id === "string")
    .map((t) => {
      const v = tagVal(t, tier);
      return v !== 0 ? { id: t.id, val: v } : { id: t.id };
    });
}

/**
 * Resolve an NPC's **tier notation** tag val — `"{2/3/4}"`, the exact string the printed
 * statblock uses, meaning *2 at Tier 1, 3 at Tier 2, 4 at Tier 3*. Returns `undefined` for
 * anything that isn't this shape, so the caller falls through to its ordinary numeric parse.
 *
 * **This is deliberately a narrow, anchored pattern, not a general "string val" handler.**
 * A tag `val` can legitimately be a non-numeric string that means something else entirely:
 * `tg_limited` on `mw_prototype_1/2/3` carries a **dice expression** (`"1d6+2"`, `"2d6"` —
 * RAW's variable, once-rolled Limited charge pool), which is a *separate* known defect with
 * its own scope and must NOT be swept into this path — a dice expression is not a per-tier
 * triple, and resolving it here would pick a number RAW never prints. Matching only `{a/b/c}`
 * keeps the two lanes disjoint: the dice expressions don't match, so their (wrong, but
 * separately-tracked) `parseInt` truncation is untouched by this function.
 *
 * The three real carriers in the official NPC data are Assault's Heavy Assault Rifle
 * `{2/3/4}`, Archer's Light Machine Gun `{3/4/5}` and Rainmaker's Missile Pods `{2/3/3}` —
 * every one of them `tg_reliable`, and the only tier-notation tag vals in either pack layout.
 *
 * **A tier is required to resolve one**, and the NPC freeze always has it (`freezeNpcWeapon`
 * takes it; `spawnNpc` threads it to the display/system freezes too). Without a tier there is
 * no honest answer, so this returns `undefined` and the val drops rather than guessing a tier —
 * the mech/pilot-gear paths that lack a tier also carry no tier-notation data, so nothing real
 * lands there.
 */
export function tierNotationVal(val: unknown, tier: NpcTier | undefined): number | undefined {
  if (typeof val !== "string" || tier === undefined) return undefined;
  const m = val.trim().match(/^\{\s*(-?\d+)\s*\/\s*(-?\d+)\s*\/\s*(-?\d+)\s*\}$/);
  if (!m) return undefined;
  const n = parseInt(m[tier]!, 10);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Read a numeric `tg_reliable`-style tag value (the `val` on the TagRef); `0` if absent.
 * A tier-notation string (`"{2/3/4}"`) resolves against `tier` first — see
 * {@link tierNotationVal} for why that check is narrow and why the dice-expression
 * `tg_limited` vals deliberately still fall through to `parseInt`.
 */
export function tagVal(tag: { val?: unknown } | undefined, tier?: NpcTier): number {
  if (!tag) return 0;
  const v = tag.val;
  if (typeof v === "number" && Number.isFinite(v)) return Math.trunc(v);
  if (typeof v === "string") {
    const tiered = tierNotationVal(v, tier);
    if (tiered !== undefined) return tiered;
    const n = parseInt(v, 10);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

/**
 * Build a frozen {@link WeaponProfile} from a compendium weapon. Reads every damage entry
 * (first → primary `damage`, rest → `riders`) and the mitigation tags (`tg_ap`, `tg_overkill`,
 * `tg_reliable {N}`, `tg_heat_self {N}`, `tg_recharge {N}`, `tg_loading`/`tg_smart`/
 * `tg_ordnance`, `tg_limited {N}`). The caller supplies the `id`/`name` fallbacks (the slot may
 * know them when the weapon item is sparse) and the frozen `instanceId` — derived from the
 * weapon's position in its source; this function never invents one.
 *
 * `instanceId` is stamped onto the returned profile **and every alternate firing
 * profile** in `profiles` — an alt profile is a different firing *mode* of the same
 * physical weapon copy, not a separate instance, so selecting one via
 * {@link selectWeaponProfile} must not change "which weapon fired."
 */
export function freezeWeaponProfile(weapon: RawWeapon, fallbackId: string, instanceId?: string): WeaponProfile {
  const id = typeof weapon.id === "string" ? weapon.id : fallbackId;
  const name = typeof weapon.name === "string" ? weapon.name : fallbackId;
  // A multi-profile weapon in lancer-data (Assault Cannon, Leviathan, Siege Cannon, Sherman
  // integrated) carries no top-level damage/range: its numbers live on `profiles`. The base entry
  // is what an attack naming no profile fires, so it takes its first (default) profile's numbers
  // rather than freezing a 0-damage, rangeless shot.
  const defaultProfile = weapon.damage === undefined ? weapon.profiles?.[0] : undefined;
  const core = freezeProfileCore({
    rawDamage: defaultProfile?.damage ?? weapon.damage,
    rawTags: defaultProfile?.tags ?? weapon.tags,
    rawRange: (defaultProfile ? defaultProfile.range : undefined) ?? weapon.range,
    id,
    name,
    instanceId,
  });

  // Freeze alternate firing profiles — each a full profile with
  // a derived `${id}__${slug(name)}` id the overlay's `select` references. A profile
  // with an "N/A"/"Cannot Be Fired" damage parses to a zero-damage profile (faithful —
  // it isn't a selectable attack tier; the `select` leaves the die there as the
  // unfireable default). A weapon with no `profiles` omits the field (the common case).
  const altProfiles = (weapon.profiles ?? [])
    .filter((p) => typeof p.name === "string")
    .map((p) => {
      const pName = String(p.name);
      const alt = freezeProfileCore({
        rawDamage: p.damage,
        rawTags: p.tags ?? weapon.tags,
        rawRange: p.range ?? weapon.range,
        id: weaponProfileId(id, pName),
        name: pName,
        instanceId,
      });
      return p.skirmish === true ? { ...alt, skirmish: true as const } : alt;
    });

  // lancer-data's `skirmish: true` (the Leviathan's Standard profile, Core p.134). The base weapon
  // takes it from its own entry, else from its first profile — the default firing mode an attack
  // naming no profile means.
  const skirmish = weapon.skirmish === true || (weapon.skirmish === undefined && weapon.profiles?.[0]?.skirmish === true);

  // Freeze the mount SIZE (project barrage) — the Superheavy gate reads it off the frozen
  // combatant, the same `mount` string the build-time loadout invariant reads. Only a
  // well-formed string survives; absent/non-string omits the field (a normal weapon).
  const mount = typeof weapon.mount === "string" && weapon.mount.trim() ? weapon.mount : undefined;

  // Freeze the weapon CLASS — the `weapon_type` predicate
  // op's one source. ONE site, same as `mount`: this is `freezeWeaponProfile`'s single
  // top-level return, which every PC-weapon call site (mount/loadout + `integrated:N` via
  // `freezeWeapons`, `pilotgear:N` via `freezePilotGearWeapons`) routes through — never
  // duplicated per-namespace. Like `mount`, NOT applied to `altProfiles` (a firing-mode
  // profile shares its parent's weapon class; the compendium data never carries a
  // per-profile `type` either). `normalizeWeaponType` is the landmine guard for pilot gear's
  // reused `type` field (see its own doc comment).
  const weaponType = normalizeWeaponType(weapon.type);

  return {
    ...core,
    ...(altProfiles.length > 0 ? { profiles: altProfiles } : {}),
    ...(mount !== undefined ? { mount } : {}),
    ...(skirmish ? { skirmish: true as const } : {}),
    ...(weapon.no_attack === true ? { noAttack: true as const } : {}),
    ...(weaponType !== undefined ? { type: weaponType } : {}),
  };
}

/** Parse one raw `{ type, val }` damage entry into a frozen {@link WeaponDamage}. */
function parseDamageEntry(entry: { type?: unknown; val?: unknown } | undefined): WeaponDamage {
  const parsed = parseDamageVal(entry?.val);
  return {
    count: parsed.count,
    sides: parsed.sides,
    bonus: parsed.bonus,
    type: normalizeDamageType(entry?.type),
  };
}

/** Freeze the core (damage + flattened mitigation tags + frozen tag refs) of one profile. */
type FreezeProfileCoreArgs = {
  rawDamage: RawWeapon["damage"];
  rawTags: RawWeapon["tags"];
  rawRange: RawWeapon["range"];
  id: string;
  name: string;
  /** See {@link WeaponProfile.instanceId} — threaded through verbatim, never derived here. */
  instanceId?: string;
  /**
   * The owning NPC's tier, when there is one — the only thing that can resolve a tier-notation
   * tag val (`tg_reliable "{2/3/4}"`; see {@link tierNotationVal}). Set by {@link freezeNpcWeapon};
   * absent on the mech/pilot-gear paths, which have no tier and carry no such data.
   */
  tier?: NpcTier;
};

function freezeProfileCore(args: FreezeProfileCoreArgs): WeaponProfile {
  const { rawDamage, rawTags, rawRange, id, name, instanceId, tier } = args;
  // Every `damage[]` entry freezes — the first is the to-hit-linked primary, the rest are
  // riders, both in source order (replay byte-identity). An entry-less weapon still freezes an
  // all-zero primary.
  const entries = rawDamage ?? [];
  const damage: WeaponDamage = parseDamageEntry(entries[0]);
  const riders = entries.slice(1).map(parseDamageEntry);

  const tags = rawTags ?? [];
  const tagById = (tid: string) => tags.find((t) => t.id === tid);
  const frozenTags = freezeTagRefs(rawTags, tier);

  const range = parseRange(rawRange);

  const selfHeat = tagVal(tagById("tg_heat_self"), tier);
  const recharge = tagVal(tagById("tg_recharge"), tier);
  const loading = tagById("tg_loading") !== undefined;
  const smart = tagById("tg_smart") !== undefined;
  const ordnance = tagById("tg_ordnance") !== undefined;
  const sidearm = tagById("tg_sidearm") !== undefined;
  // Limited: `tg_limited {N}` is the charge count, flattened exactly like `tg_recharge {N}`
  // — a present-tag-but-zero-val
  // reads as untagged (RAW never prints "Limited 0"; the tag itself always carries N≥1).
  const limited = tagVal(tagById("tg_limited"), tier);

  return {
    id,
    name,
    damage,
    ...(riders.length > 0 ? { riders } : {}),
    ap: tagById("tg_ap") !== undefined,
    overkill: tagById("tg_overkill") !== undefined,
    reliable: tagVal(tagById("tg_reliable"), tier),
    ...(instanceId !== undefined ? { instanceId } : {}),
    ...(range.length > 0 ? { range } : {}),
    ...(frozenTags.length > 0 ? { tags: frozenTags } : {}),
    ...(selfHeat > 0 ? { selfHeat } : {}),
    ...(recharge > 0 ? { recharge } : {}),
    ...(loading ? { loading } : {}),
    ...(smart ? { smart } : {}),
    ...(ordnance ? { ordnance } : {}),
    ...(sidearm ? { sidearm } : {}),
    ...(limited > 0 ? { limited } : {}),
  };
}

/**
 * A pilot's Fight once-per-turn **allowance override**: carrying at least one `tg_sidearm`-tagged
 * weapon lets a pilot FIGHT up to **twice** a turn — RAW only says Sidearm makes Fight a Quick
 * action, not that it grants a second use, but with a Quick Fight costing 1 of the turn's 2 points,
 * a Sidearm-armed pilot naturally fits two into one turn's budget where a Full (non-Sidearm) Fight
 * never could. Modeled as data on the allowance seam rather than a special-cased branch — the
 * ordinary action-point budget still caps a non-Sidearm Full Fight to once regardless of this
 * override, so raising it here never lets a Full Fight repeat. Called at pilot-combatant mint
 * time by both spawn paths that freeze a pilot's weapons (`spawn.ts`'s `spawnPilot`,
 * `pilotBridge.ts`'s `spawnDismountedPilot`) so `base.actionAllowances` carries the SAME override
 * regardless of which path minted the combatant. Returns `undefined` (no override — the ordinary
 * once-per-turn default) when no Sidearm weapon is frozen onto the loadout, so a gearless or
 * non-Sidearm pilot's `base.actionAllowances` stays unset, exactly like every pre-Phase-5 pilot.
 */
export function sidearmFightAllowance(weapons: WeaponProfile[]): Record<string, number> | undefined {
  return weapons.some((w) => w.sidearm) ? { fight: 2 } : undefined;
}

// ── NPC features (npc-spawn-loadout) ──────────────────────────────────────────────

/**
 * The (loosely-typed, `.passthrough()`) NPC-feature fields the spawn freeze reads.
 * NPC features are first-class compendium items (`npc_feature`) whose rich fields
 * (`type`/`damage`/`range`/`attack_bonus`/`effect`/`base`/`origin`/`actions`) survive
 * untyped through the schema's passthrough — so we read them off this structural type.
 */
export type RawNpcFeature = {
  id?: string;
  name?: string;
  /** `"Weapon" | "System" | "Trait" | "Reaction" | "Tech" | …` (capitalized in the data). */
  type?: string;
  /** v3 denormalized "this is a base (always-on) feature" flag. */
  base?: boolean;
  /** v3's tombstone flag: the feature's text says it is folded into another and can be removed. Never offered as a pick. */
  deprecated?: boolean;
  /** The owning class/template id (`"npcc_ace"`). */
  origin?: string;
  /** Display rule text. */
  effect?: unknown;
  tags?: Array<{ id?: unknown; val?: unknown }>;
  /**
   * NPC weapon damage — one or more `{type, val}` entries, each independently a
   * flat per-tier `val` (`[4,6,8]`) or a tier-invariant scalar. First entry freezes
   * to `damage`, the rest to `riders`.
   */
  damage?: Array<{ type?: unknown; val?: unknown }>;
  /** Per-tier (or scalar) to-hit bonus. */
  attack_bonus?: number | number[];
  /**
   * The weapon's **innate Accuracy/Difficulty** (RAW Core p.281 — printed as e.g. "ACCURACY
   * 2" on the stat block; a negative value is Difficulty). Per-tier (Ram Cannon `[1,2,3]`) or
   * scalar (AMR `2`, Bombard Cannon `-1`) — tier-resolved by {@link freezeNpcWeapon} exactly
   * like {@link attack_bonus}. Absent for a weapon with no printed innate number (Combat
   * Knife) — never a `0` sentinel, since a scalar `0` isn't a value RAW ever prints here
   * anyway (every real innate number is nonzero).
   */
  accuracy?: number | number[];
  /**
   * The weapon's class line (`"Main Rifle"`, `"Heavy Melee"`). A slash names a weapon usable in
   * two classes (`"Heavy Melee/Heavy Cannon"`, the Ram Cannon); {@link freezeNpcWeapon} reads it
   * for {@link WeaponProfile.eitherMode} and for the weapon's SIZE ({@link WeaponProfile.mount},
   * the line's leading word — the Superheavy Skirmish/Overwatch gate reads it).
   */
  weapon_type?: unknown;
  /** Range bands (`[{type:"Range",val:10},{type:"Blast",val:1}]`); `val` may be per-tier (`[10,12,15]`). Tier-resolved, then frozen as structured `WeaponProfile.range` (attack-range overlay). */
  range?: Array<{ type?: unknown; val?: unknown }>;
  /** Action blocks (the rule text lives here when there's no top-level `effect`). */
  actions?: Array<{ name?: unknown; detail?: unknown }>;
  /** Deployables (Razor Swarms' swarm); each mints a deploy affordance at spawn, after the actions. */
  deployables?: Array<{ name?: unknown; activation?: unknown; detail?: unknown }>;
  /**
   * How many attacks one use of the weapon makes, per tier (`[1,2,3]`, the Drum Shotgun) or
   * scalar (`2`, the Dual Shotguns). Tier-resolved by {@link freezeNpcWeapon} into
   * {@link WeaponProfile.attacks}.
   */
  attacks?: number | number[];
  /**
   * Top-level stat-modifying bonuses (`npc-deploy-composition`, decision 1) — the v3 pack's
   * data-driven stacking entries (`{id, val, overwrite?, replace?}`), read verbatim by the
   * shared `applyBonus` engine (`../bonus`). **Top-level only** — a bonus nested under
   * `active_effects[].bonuses` (e.g. Argus Armor's Armor-loss-on-crit rider) is a *conditional*
   * trigger effect, out of scope for the unconditional NPC bonus pre-pass; only this field is
   * read. `val` may be a per-tier array (`[2,4,6]`) — tier-resolve via {@link tierResolve}
   * BEFORE handing it to `applyBonus` (that engine's own array handling *sums* multiple
   * formula terms, a different semantic than "pick this tier's entry").
   */
  bonuses?: Bonus[];
}

/** True if an NPC feature is a Weapon (freezes into resolvable `base.weapons`, not the display list). */
export function isNpcWeaponFeature(feature: RawNpcFeature): boolean {
  return typeof feature.type === "string" && feature.type.toLowerCase() === "weapon";
}

/**
 * Tier-resolve a `scalar | [t1,t2,t3]` value: an array indexes by `tier-1`, a scalar is
 * tier-invariant. Exported for the NPC bonus pre-pass (`npc-deploy-composition`,
 * decision 2) — a bonus's `val` can itself be a per-tier array (`npcf_veteran_human`'s
 * `hp: [2,4,6]`), which must be resolved to a scalar *before* `applyBonus`/`evaluateModifier`
 * see it (their own array handling sums multiple formula terms, not "pick tier N").
 */
export function tierResolve(v: unknown, tier: NpcTier): unknown {
  return Array.isArray(v) ? v[tier - 1] : v;
}

/** Tier-resolve a numeric `attack_bonus` (`scalar | [t1,t2,t3]`); `undefined` when absent/non-numeric. */
export function tierResolveNumber(v: number | number[] | undefined, tier: NpcTier): number | undefined {
  const at = tierResolve(v, tier);
  return typeof at === "number" && Number.isFinite(at) ? at : undefined;
}

/**
 * Tier-resolve an NPC weapon's range bands into the raw `{type, val}` shape
 * {@link parseRange}/{@link freezeProfileCore} consume. An NPC band's `val` can be
 * **per-tier** (`[10,12,15]`) where a mech weapon's is always scalar, so each band's
 * `val` is tier-resolved to a scalar here before the shared core parses it into the
 * structured {@link WeaponRange} entries that drive the VTT's attack-range overlay.
 */
function tierResolveRangeBands(range: RawNpcFeature["range"], tier: NpcTier): RawWeapon["range"] {
  if (!Array.isArray(range)) return undefined;
  return range.map((band) => ({ type: band.type, val: tierResolve(band.val, tier) }));
}

/**
 * Freeze an NPC **Weapon** feature into a resolvable {@link WeaponProfile} at a tier. NPC
 * weapon damage entries are **independently** either flat per-tier (`val:[4,6,8]`) or
 * tier-invariant scalar (`val:3`) — Ultra Hellfire Projector mixes a scalar Energy
 * primary with a per-tier Burn rider in the same `damage[]` — so every entry is
 * tier-resolved **on its own** before parsing, never assuming the whole array shares
 * one shape. The tier-resolved entries are handed to the shared core as a synthetic
 * `rawDamage` array, which freezes the first to `damage` and the rest to `riders`
 * (source order) exactly like a mech weapon's multi-entry damage. The NPC's range
 * bands are likewise tier-resolved and handed to the shared core as `rawRange`, so the
 * weapon freezes the same structured `range?: WeaponRange[]` a mech weapon does — no
 * NPC-specific range model. Delegates tag flattening to
 * {@link freezeProfileCore}, then attaches the per-tier `attackBonus` (the NPC stat
 * block carries no class-level bonus) and the `effect` display text.
 *
 * `instanceId` is the caller-supplied instance id —
 * NPC weapon features aren't mount/slot-indexed like a mech loadout, so the spawn adapter
 * derives it from the feature's position in the class's feature list (`npc:<index>`)
 * and passes it through verbatim; this function never invents one.
 */
export function freezeNpcWeapon(feature: RawNpcFeature, tier: NpcTier, instanceId?: string): WeaponProfile {
  const id = typeof feature.id === "string" ? feature.id : "npc_weapon";
  const name = typeof feature.name === "string" ? feature.name : id;
  // Tier-resolve EVERY entry's val independently, then hand the whole resolved array
  // to the shared core — it parses entry 0 as `damage`, the rest as `riders`.
  const resolvedDamage = (feature.damage ?? []).map((e) => ({ type: e?.type, val: tierResolve(e?.val, tier) }));
  const rawRange = tierResolveRangeBands(feature.range, tier);
  const core = freezeProfileCore({ rawDamage: resolvedDamage, rawTags: feature.tags, rawRange, id, name, instanceId, tier });

  const attackBonus = tierResolveNumber(feature.attack_bonus, tier);
  const accuracy = tierResolveNumber(feature.accuracy, tier);
  const effect = typeof feature.effect === "string" && feature.effect.trim() ? feature.effect : undefined;
  const mount = npcWeaponSize(feature.weapon_type);
  const fxClass = npcWeaponClass(feature.weapon_type);
  const attacks = NPC_ATTACKS_COUNT_TARGETS.has(id) ? undefined : tierResolveNumber(feature.attacks, tier);

  return {
    ...core,
    ...(attackBonus !== undefined ? { attackBonus } : {}),
    ...(accuracy !== undefined ? { accuracy } : {}),
    ...(effect !== undefined ? { effect } : {}),
    ...(isEitherModeWeaponType(feature.weapon_type) ? { eitherMode: true as const } : {}),
    ...(mount !== undefined ? { mount } : {}),
    ...(fxClass !== undefined ? { fxClass } : {}),
    ...(attacks !== undefined && attacks > 1 ? { attacks } : {}),
  };
}

/**
 * NPC weapons whose `attacks` field counts the **targets of one attack**, not separate attacks.
 * The Missile Pods' `attacks: [3,3,3]` prints in Core as "This weapon can attack 1-3 characters
 * at a time": one attack against up to three characters, a roll per target and one damage roll
 * (the Sharanga Missiles' shape). Its overlay entry authors that as `attacksTargets: 3`, so the
 * count is not frozen as {@link WeaponProfile.attacks}.
 */
const NPC_ATTACKS_COUNT_TARGETS: ReadonlySet<string> = new Set(["npcf_missile_pods_rainmaker"]);

const NPC_WEAPON_SIZES: ReadonlySet<string> = new Set(["Auxiliary", "Main", "Heavy", "Superheavy"]);

/**
 * The weapon SIZE an NPC `weapon_type` line leads with (`"Superheavy Rifle"` → `"Superheavy"`),
 * frozen as {@link WeaponProfile.mount} — the same field a mech weapon's compendium `mount` size
 * freezes to, so the Superheavy gates read one field for both. A slash-joined line takes its
 * first class's size (every fixture line agrees across the slash). `undefined` for an empty or
 * unrecognized line.
 */
function npcWeaponSize(weaponType: unknown): string | undefined {
  if (typeof weaponType !== "string") return undefined;
  const size = weaponType.trim().split(/\s+/)[0] ?? "";
  return NPC_WEAPON_SIZES.has(size) ? size : undefined;
}

/**
 * The class word an NPC `weapon_type` line ends with (`"Superheavy Rifle"` → `"Rifle"`), frozen as
 * {@link WeaponProfile.fxClass} for the attack fx rule. A slash-joined line takes its first
 * non-Melee class (`"Heavy Melee/Heavy Cannon"` → `"Cannon"`): the weapon's melee mode is resolved
 * per attack, so the class word only matters when it fires at range. `undefined` for an empty line
 * (the NPC template generics).
 */
function npcWeaponClass(weaponType: unknown): string | undefined {
  if (typeof weaponType !== "string") return undefined;
  const classes = weaponType.split("/").map((c) => c.trim().split(/\s+/).at(-1) ?? "").filter((c) => c !== "");
  return classes.find((c) => c !== "Melee") ?? classes[0];
}

/**
 * Does an NPC `weapon_type` name a weapon usable as melee OR ranged? True for a slash-joined
 * line with one Melee class and one non-Melee class (`"Heavy Melee/Heavy Cannon"`). In the v3
 * fixture only the Ram Cannon matches; its prose says "either a ranged or melee weapon, but not
 * both in the same turn", which the structured field and the prose agree on (`decisions/0122`).
 */
function isEitherModeWeaponType(weaponType: unknown): boolean {
  if (typeof weaponType !== "string") return false;
  const classes = weaponType.split("/").map((c) => c.trim().toLowerCase());
  if (classes.length < 2) return false;
  const melee = classes.filter((c) => c.endsWith("melee")).length;
  return melee > 0 && melee < classes.length;
}

/** The feature's primary display text: its `effect` prose, else the first action's `detail`. */
function displayEffect(feature: RawNpcFeature): string | undefined {
  if (typeof feature.effect === "string" && feature.effect.trim()) return feature.effect;
  const detail = feature.actions?.[0]?.detail;
  return typeof detail === "string" && detail.trim() ? detail : undefined;
}

/**
 * Freeze the **non-weapon** NPC features (System / Trait / Reaction / Tech) into the
 * display-only {@link FrozenNpcFeature} list (npc-spawn-loadout). Pure presentation —
 * the resolver never reads it; the inspector renders the NPC's stat block from it.
 * Each carries its `name`, `type`, primary rule `effect`, and frozen `tags`.
 *
 * `tier` is the spawning NPC's tier, taken for the same reason {@link freezeNpcWeapon} takes
 * one — a tier-notation tag val (`"{2/3/4}"`) can only resolve against it. No non-weapon
 * feature in the official data carries one today, but this list freezes through the SAME
 * {@link freezeTagRefs} the resolvable weapons do, and a stat-block row printing a different
 * Reliable number from the weapon it describes would be its own bug.
 */
export function freezeDisplayFeatures(features: RawNpcFeature[], tier?: NpcTier): FrozenNpcFeature[] {
  return features.map((f) => {
    const name = typeof f.name === "string" ? f.name : (typeof f.id === "string" ? f.id : "Feature");
    const type = typeof f.type === "string" ? f.type : "Trait";
    const effect = displayEffect(f);
    const tags = freezeTagRefs(f.tags, tier);
    return {
      ...(typeof f.id === "string" ? { id: f.id } : {}),
      name,
      type,
      ...(effect !== undefined ? { effect } : {}),
      ...(tags.length > 0 ? { tags } : {}),
    };
  });
}
